import React, { useContext, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { IoClose } from "react-icons/io5";
import { FaChartBar, FaTable, FaDotCircle, FaChartPie, FaDownload, FaFilter } from "react-icons/fa";
import * as XLSX from 'xlsx';
import Constants from "../../Constants";
import { PeFilterContext } from "../../Contexts/PeFilterContext";
import moment from "moment";

const PERIOD_OPTIONS = [
  { value: 'CY', label: 'CY' },
  { value: 'FY', label: 'FY' },
];

const INTERVAL_OPTIONS = [
  { value: 'yearly', label: 'Yearly' },
  { value: 'half-yearly', label: 'Half-Yearly' },
  { value: 'quarterly', label: 'Quarterly' }
  // { value: 'monthly', label: 'Monthly' },
];

const ChartPopup = ({ isOpen, onClose, data, isLoading = false, subType="", showBy = '', availableShowByOptions = [], onShowByChange, menuType = '', interval = 'yearly', onIntervalChange, period = 'CY', onPeriodChange, redirectPath = '' }) => {
  const [viewMode, setViewMode] = useState("table"); // "chart", "datapoints", or "table"
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [valueBy, setValueBy] = useState("both"); // "deals" or "amount"
  const { monthPickerValue, setOpen, open, refineSearchFilters } = useContext(PeFilterContext);

  // Number of refine search filters currently applied on this page (shown as a badge on the button)
  const activeFilterCount = Object.entries(refineSearchFilters?.[redirectPath] || {})
    .filter(([key, value]) => key !== 'globalSearch' && Array.isArray(value) && value.length > 0)
    .length;
  
  // Helper function to detect mobile viewport
  const isMobileView = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768; // Tailwind's md breakpoint
  };

  const [isMobile, setIsMobile] = useState(isMobileView());

  // Detect mobile view changes on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileView());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Download icon SVG for ApexCharts toolbar - simple and clean design
  const downloadIconSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512" fill="#000000"><path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/></svg>';
  
  // Compute exact date range for a clicked period label
  const getDateRangeForLabel = (label, periodType) => {
    const str = String(label);

    const monthMap = {
      Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
      Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
    };
    const lastDay = (year, month) =>
      moment(`${year}-${String(month).padStart(2, '0')}-01`).endOf('month').format('YYYY-MM-DD');
    const pad = (n) => String(n).padStart(2, '0');

    if (periodType === 'FY') {
      // Months Apr-Dec (>= 4) sit in fyStartYear; Jan-Mar (< 4) sit in fyEndYear
      const fyYearFor = (monthNum, fyStartYear, fyEndYear) =>
        monthNum >= 4 ? fyStartYear : fyEndYear;
      const fyEndYearFrom = (raw) =>
        raw.length === 2 ? parseInt(`20${raw}`) : parseInt(raw);

      // ── 0th / nth index: labels carry actual month range in parentheses ──

      // Yearly FY: "2022-23 (Jun - Mar)"
      const yearlyWithMonths = str.match(/^(\d{4})-(\d{2,4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (yearlyWithMonths) {
        const fyStartYear = parseInt(yearlyWithMonths[1]);
        const raw = yearlyWithMonths[2];
        const fyEndYear = raw.length === 2
          ? parseInt(`${String(fyStartYear).slice(0, 2)}${raw}`)
          : parseInt(raw);
        const startM = monthMap[yearlyWithMonths[3]];
        const endM   = monthMap[yearlyWithMonths[4]];
        return [`${fyStartYear}-${pad(startM)}-01`, lastDay(fyEndYear, endM)];
      }

      // Quarterly FY: "Q1 FY23 (Jun - Jun)"
      const quarterlyWithMonths = str.match(/^Q\d\s*FY(\d{2,4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (quarterlyWithMonths) {
        const fyEndYear   = fyEndYearFrom(quarterlyWithMonths[1]);
        const fyStartYear = fyEndYear - 1;
        const startM = monthMap[quarterlyWithMonths[2]];
        const endM   = monthMap[quarterlyWithMonths[3]];
        return [
          `${fyYearFor(startM, fyStartYear, fyEndYear)}-${pad(startM)}-01`,
          lastDay(fyYearFor(endM, fyStartYear, fyEndYear), endM),
        ];
      }

      // Half-yearly FY: "H1 FY23 (Jun - Sep)"
      const halfYearlyWithMonths = str.match(/^H[12]\s*FY(\d{2,4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (halfYearlyWithMonths) {
        const fyEndYear   = fyEndYearFrom(halfYearlyWithMonths[1]);
        const fyStartYear = fyEndYear - 1;
        const startM = monthMap[halfYearlyWithMonths[2]];
        const endM   = monthMap[halfYearlyWithMonths[3]];
        return [
          `${fyYearFor(startM, fyStartYear, fyEndYear)}-${pad(startM)}-01`,
          lastDay(fyYearFor(endM, fyStartYear, fyEndYear), endM),
        ];
      }

      // ── Middle indices: plain labels, full period ──

      // Yearly FY: "2023-24"
      const yearlyPlain = str.match(/^(\d{4})-(\d{2,4})$/);
      if (yearlyPlain) {
        const fyStartYear = yearlyPlain[1];
        const raw = yearlyPlain[2];
        const fyEndYear = raw.length === 2
          ? `${fyStartYear.slice(0, 2)}${raw}`
          : raw;
        return [`${fyStartYear}-04-01`, `${fyEndYear}-03-31`];
      }

      // Quarterly FY: "Q1 FY24"
      const quarterlyPlain = str.match(/^(Q[1-4])\s*FY(\d{2,4})$/);
      if (quarterlyPlain) {
        const q = quarterlyPlain[1];
        const fyEndYear   = fyEndYearFrom(quarterlyPlain[2]);
        const fyStartYear = fyEndYear - 1;
        const qRanges = {
          Q1: [`${fyStartYear}-04-01`, `${fyStartYear}-06-30`],
          Q2: [`${fyStartYear}-07-01`, `${fyStartYear}-09-30`],
          Q3: [`${fyStartYear}-10-01`, `${fyStartYear}-12-31`],
          Q4: [`${fyEndYear}-01-01`,   `${fyEndYear}-03-31`],
        };
        if (qRanges[q]) return qRanges[q];
      }

      // Half-yearly FY: "H1 FY24"
      const halfYearlyPlain = str.match(/^(H[12])\s*FY(\d{2,4})$/);
      if (halfYearlyPlain) {
        const h = halfYearlyPlain[1];
        const fyEndYear   = fyEndYearFrom(halfYearlyPlain[2]);
        const fyStartYear = fyEndYear - 1;
        return h === 'H1'
          ? [`${fyStartYear}-04-01`, `${fyStartYear}-09-30`]
          : [`${fyStartYear}-10-01`, `${fyEndYear}-03-31`];
      }

    } else {
      // CY (Calendar Year)
      const ytdEnd = moment().endOf("month").format("YYYY-MM-DD");
      const capEnd = (end) => moment(end).isAfter(moment()) ? ytdEnd : end;

      // ── 0th / nth index: labels carry actual month range in parentheses ──

      // Yearly CY: "2022 (Jun - Dec)"
      const yearlyWithMonths = str.match(/^(\d{4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (yearlyWithMonths) {
        const year   = yearlyWithMonths[1];
        const startM = monthMap[yearlyWithMonths[2]];
        const endM   = monthMap[yearlyWithMonths[3]];
        return [`${year}-${pad(startM)}-01`, capEnd(lastDay(year, endM))];
      }

      // Half-yearly CY: "H1 2022 (Jun - Jun)"
      const halfYearlyWithMonths = str.match(/^H[12]\s*(\d{4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (halfYearlyWithMonths) {
        const year   = halfYearlyWithMonths[1];
        const startM = monthMap[halfYearlyWithMonths[2]];
        const endM   = monthMap[halfYearlyWithMonths[3]];
        return [`${year}-${pad(startM)}-01`, capEnd(lastDay(year, endM))];
      }

      // Quarterly CY: "Q1 2022 (Jun - Jun)"
      const quarterlyWithMonths = str.match(/^Q\d\s*(\d{4})\s*\((\w{3})\s*-\s*(\w{3})\)$/);
      if (quarterlyWithMonths) {
        const year   = quarterlyWithMonths[1];
        const startM = monthMap[quarterlyWithMonths[2]];
        const endM   = monthMap[quarterlyWithMonths[3]];
        return [`${year}-${pad(startM)}-01`, capEnd(lastDay(year, endM))];
      }

      // ── Middle indices: plain labels ──

      // CY yearly: "2025"
      const yearMatch = str.match(/^(\d{4})$/);
      if (yearMatch) {
        const year = yearMatch[1];
        return [`${year}-01-01`, capEnd(`${year}-12-31`)];
      }

      // CY half-yearly: "H1 2025" / "H2 2025"
      const halfYearMatch = str.match(/^(H[1-2])\s*(\d{4})$/);
      if (halfYearMatch) {
        const [, half, year] = halfYearMatch;
        const [start, end] = half === 'H1'
          ? [`${year}-01-01`, `${year}-06-30`]
          : [`${year}-07-01`, `${year}-12-31`];
        return [start, capEnd(end)];
      }
      
      // CY quarterly: "Q1 2025" etc.
      const quarterMatch = str.match(/^(Q[1-4])\s*(\d{4})$/);
      if (quarterMatch) {
        const [, q, year] = quarterMatch;
        const ranges = {
          Q1: [`${year}-01-01`, `${year}-03-31`],
          Q2: [`${year}-04-01`, `${year}-06-30`],
          Q3: [`${year}-07-01`, `${year}-09-30`],
          Q4: [`${year}-10-01`, `${year}-12-31`],
        };
        if (ranges[q]) return [ranges[q][0], capEnd(ranges[q][1])];
      }
    }

    // Fallback: keep the current global picker range
    return [
      moment(monthPickerValue?.[0]).format("YYYY-MM-DD"),
      moment(monthPickerValue?.[1]).format("YYYY-MM-DD"),
    ];
  };

  // Handle deal count click - sets deal_ids and company_ids in localStorage and opens new tab
  // yearLabel: the raw period label of the clicked row (e.g. "2025", "H1 2025", "2024-25")
  const handleDealClick = (dealIds, companyIds, label, yearLabel, hide=false) => {
    const dateRange = yearLabel
      ? getDateRangeForLabel(yearLabel, period)
      : [moment(monthPickerValue?.[0]).format("YYYY-MM-DD"), moment(monthPickerValue?.[1]).format("YYYY-MM-DD")];
    const data = {
      initialSearch: {
        deal_ids: [{ value: dealIds || [], name: `${label}`, hide: hide}],
      },
      dateRange,
    };
    localStorage.setItem(Constants.localStorageKeys.referer, JSON.stringify(data));
  };

  // Check if this is angel or incubation menu type - these should only show deals
  const isAngelOrIncubation = menuType === 'angel' || menuType === 'incubation' || menuType === 'vc-angel' || menuType === 'vc-incubation';

  // Helper function to get display label for showBy value
  const getShowByLabel = (showByValue) => {
    const labels = {
      'industry': 'Industry',
      'sector': 'Sector',
      'country': 'Country',
      'region': 'Region',
      'year_founded': 'Year Founded',
      'state': 'State',
      'city': 'City',
      'investor_type': 'Investor Type',
      'investor_headquaters': 'Investor Headquarters',
      'investor_headquaters_city': 'Investor HQ City',
      'firm_type': 'Firm Type',
      'tag': 'Tags',
      'ladvisors': 'Lead Advisors',
      'tadvisors': 'Transaction Advisors',
      'investor': 'Investors',
      'company': 'Target Companies',
      'stage': 'Stage',
      'round': 'Round',
      'deal_type': 'Deal Type',
      'company_type': 'Company Type',
      'syndication': 'Syndication',
      'deals_involving': 'Deals Involving',
      'exit_status': 'Exit Status',
      'valuation': 'Valuation',
      'deal_range': 'Deal Range',
      'investor_sale': 'Investor Sale',
      'vc_fund': 'VC Funded',
      'exited': 'Exited',
      'incubation_status': 'Incubation Status',
      'defunct': 'Defunct',
      // M&A specific labels
      'target_industry': 'Target Industry',
      'target_sector': 'Target Sector',
      'target_country': 'Target Country',
      'target_region': 'Target Region',
      'target_state': 'Target State',
      'target_city': 'Target City',
      'target_company': 'Target Company',
      'target_company_type': 'Target Company Type',
      'acquirer_industry': 'Acquirer Industry',
      'acquirer_country': 'Acquirer Country',
      'acquirer_region': 'Acquirer Region',
      'acquirer_state': 'Acquirer State',
      'acquirer_city': 'Acquirer City',
      'acquirer_company_type': 'Acquirer Company Type',
      'acquirer_group': 'Acquirer Group',
      'company_group': 'Company Group',
      'acquirer': 'Acquirer',
      'year': 'Year',
    };
    return labels[showByValue] || showByValue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isOpen) return null;

  // Safely extract chart data with proper error handling
  const chartDataArray = data?.data?.dataset || [];
  const summaryData = data?.data?.summary || {};
  
  console.log("ChartPopup received data:", data); // Debug log
  console.log("Chart data array:", chartDataArray); // Debug log
  console.log("showBy prop:", showBy); // Debug log
  console.log("availableShowByOptions:", availableShowByOptions); // Debug log
  
  // Check if data has breakdown (grouping) or just totals
  // If showBy is empty or availableShowByOptions is empty, treat as no breakdown
  const hasBreakdown = showBy && availableShowByOptions.length > 0 && chartDataArray.some(item => item.breakdown && item.breakdown.length > 0);
  console.log("Has breakdown:", hasBreakdown); // Debug log

  const isShowByYear = showBy.toLocaleLowerCase() === 'year'
  
  // Check if total amount or deals is 0
  const totalAmount = summaryData?.totals?.deal_amount || 0;
  const totalDeals = summaryData?.totals?.deal_count || 0;
  const hasAmount = totalAmount > 0;
  const hasDeals = totalDeals > 0;
  
  // Sort chart data by period label (handles both numeric years and string labels like "H1 2025", "Q1 2024")
  const sortedChartDataArray = [...chartDataArray].sort((a, b) => {
    const strA = String(a.label), strB = String(b.label);
    const numA = parseFloat(strA), numB = parseFloat(strB);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    // Non-numeric: extract 4-digit year, then sort within year by period label
    const yearA = parseInt((strA.match(/\d{4}/) || [])[0]) || 0;
    const yearB = parseInt((strB.match(/\d{4}/) || [])[0]) || 0;
    if (yearA !== yearB) return yearA - yearB;
    return strA.localeCompare(strB);
  });
  
  const sortedChartCategories = sortedChartDataArray.map(item => item.label);
  const yearCount = sortedChartCategories.length
  
  // Create color mapping for different industry groups
  const getColorByGroup = (group) => {
    // Define a palette of distinct colors (excluding red and green)
    const colorPalette = [
      "#3b82f6", "#f59e0b", "#8b5cf6", "#f97316", "#06b6d4", 
      "#ec4899", "#6366f1", "#a855f7", "#0ea5e9", "#d946ef",
      "#4338ca", "#9333ea", "#0891b2", "#c2410c", "#7c2d12",
      "#be185d", "#1e40af", "#7c3aed", "#0284c7", "#ea580c",
      "#db2777", "#7e22ce", "#0369a1", "#c2410c", "#a21caf"
    ];
    
    // Get all unique groups first to maintain consistent color assignment
    const allUniqueGroups = [];
    chartDataArray.forEach(item => {
      item.breakdown?.forEach(breakdownItem => {
        if (!allUniqueGroups.includes(breakdownItem.group)) {
          allUniqueGroups.push(breakdownItem.group);
        }
      });
    });
    
    // Assign color based on group's index in the sorted array
    const groupIndex = allUniqueGroups.indexOf(group);
    if (groupIndex !== -1) {
      return colorPalette[groupIndex % colorPalette.length];
    }
    
    // Fallback color if group not found
    return "#9ca3af";
  };
  
  // Get all unique groups from all years for consistent series
  const allGroups = new Set();
  chartDataArray.forEach(item => {
    item.breakdown?.forEach(breakdownItem => {
      allGroups.add(breakdownItem.group);
    });
  });
  
  // Data for the stacked bar chart — aligned with chartCategories (newest first)
  const chartDataForSeries = [...sortedChartDataArray].reverse();

  // Create series data for stacked bar chart
  const chartSeries = Array.from(allGroups).map(group => ({
    name: group,
    data: chartDataForSeries.map(item => {
      const breakdownItem = item.breakdown?.find(b => b.group === group);
      if (valueBy === "amount") {
        return breakdownItem ? (breakdownItem.deal_amount || 0) : 0;
      } else {
        return breakdownItem ? (breakdownItem.deal_count || 0) : 0;
      }
    }),
    color: getColorByGroup(group)
  }));

  // Create separate series for amount when showing both
  const amountChartSeries = Array.from(allGroups).map(group => ({
    name: group,
    data: chartDataForSeries.map(item => {
      const breakdownItem = item.breakdown?.find(b => b.group === group);
      return breakdownItem ? (breakdownItem.deal_amount || 0) : 0;
    }),
    color: getColorByGroup(group)
  }));

  // Create separate series for deals when showing both
  const dealsChartSeries = Array.from(allGroups).map(group => ({
    name: group,
    data: chartDataForSeries.map(item => {
      const breakdownItem = item.breakdown?.find(b => b.group === group);
      return breakdownItem ? (breakdownItem.deal_count || 0) : 0;
    }),
    color: getColorByGroup(group)
  }));


  
  // Categories for the stacked bar chart Y-axis — newest first (sorted desc) so latest period appears at top
  const chartCategories = [...sortedChartCategories].reverse();

  // Format period label based on selected period (e.g., "2022-23" → "FY23" when period is "FY")
  const formatPeriodLabel = (label) => {
    const str = String(label);
    
    // If not FY mode, return as-is
    if (period !== 'FY') {
      return str;
    }
    
    // FY mode formatting
    // Check for quarterly format (Q1 2025, Q2 2025, etc.)
    const quarterMatch = str.match(/^(Q[1-4])\s*(\d{4})$/);
    if (quarterMatch) {
      return `${quarterMatch[1]} FY${quarterMatch[2].slice(-2)}`;
    }
    
    // Check for half-yearly format (H1 2025, H2 2025, etc.)
    const halfYearMatch = str.match(/^(H[1-2])\s*(\d{4})$/);
    if (halfYearMatch) {
      return `${halfYearMatch[1]} FY${halfYearMatch[2].slice(-2)}`;
    }
    
    const rangeMatch = str.match(/(\d{4})-(\d{2,4})(.*)?/);

    if (rangeMatch) {
      const endYear =
        rangeMatch[2].length === 2
          ? rangeMatch[2]
          : rangeMatch[2].slice(-2);

      const suffix = rangeMatch[3]?.trim() || "";

      return `FY${endYear}${suffix ? ` ${suffix}` : ""}`;
    }
    
    // Default: just extract year and add FY prefix (for yearly)
    const match = str.match(/(\d{4})/);
    if (match) return `FY${match[1].slice(-2)}`;
    return str;
  };
  const displayChartCategories = chartCategories.map(label => formatPeriodLabel(String(label)));
  const displaySortedChartCategories = sortedChartCategories.map(label => formatPeriodLabel(String(label)));
  
  console.log("Chart Categories:", chartCategories); // Debug log
  console.log("Chart Series:", chartSeries); // Debug log
  console.log("All Groups:", Array.from(allGroups)); // Debug log

  // Sort function for table
  const handleSort = (key) => {
    let direction = 'desc'; // Default to descending (larger to smaller)
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Create table data structure for flipped layout
  const createTableData = () => {
    const tableData = [];
    const yearTotals = {};
    const yearAmountTotals = {};
    
    // Initialize year totals
    chartCategories.forEach(year => {
      yearTotals[year] = 0;
      yearAmountTotals[year] = 0;
    });
    
    // For each industry, create a row with deal counts for each year
    Array.from(allGroups).sort().forEach(industry => {
      const row = {
        industry: industry
      };
      
      // Add values for each year
      chartDataArray.forEach(yearData => {
        const industryData = yearData.breakdown?.find(item => item.group === industry);
        
        if (valueBy === "both") {
          // Store both amount and deals for each year
          const dealCount = industryData ? (industryData.deal_count || industryData.value || 0) : 0;
          const dealAmount = industryData ? (industryData.deal_amount || 0) : 0;
          
          row[`${yearData.label}_deals`] = dealCount;
          row[`${yearData.label}_amount`] = dealAmount;
          
          yearTotals[yearData.label] += dealCount;
          yearAmountTotals[yearData.label] += dealAmount;
        } else {
          // Use the appropriate value based on valueBy selection
          let value;
          if (valueBy === "amount") {
            value = industryData ? (industryData.deal_amount || 0) : 0;
          } else {
            value = industryData ? (industryData.deal_count || industryData.value || 0) : 0;
          }
          
          row[yearData.label] = value;
          yearTotals[yearData.label] += value;
        }
      });
      
      tableData.push(row);
    });
    
    // Add total row at the end
    const totalRow = {
      industry: 'Total',
      isTotal: true
    };
    
    if (valueBy === "both") {
      chartCategories.forEach(year => {
        totalRow[`${year}_deals`] = yearTotals[year];
        totalRow[`${year}_amount`] = yearAmountTotals[year];
      });
    } else {
      chartCategories.forEach(year => {
        totalRow[year] = yearTotals[year];
      });
    }
    
    tableData.push(totalRow);
    
    // Sort the data if needed (but keep total row at the end)
    if (sortConfig.key) {
      const dataWithoutTotal = tableData.slice(0, -1);
      const totalRowData = tableData[tableData.length - 1];
      
      dataWithoutTotal.sort((a, b) => {
        // Handle sorting by the industry/category column (showBy field)
        if (sortConfig.key === showBy || sortConfig.key === 'industry') {
          const aValue = a.industry || '';
          const bValue = b.industry || '';
          
          if (sortConfig.direction === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        } else {
          // Handle sorting by numeric columns (year data)
          const aValue = a[sortConfig.key] || 0;
          const bValue = b[sortConfig.key] || 0;
          
          if (sortConfig.direction === 'asc') {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        }
      });
      
      return [...dataWithoutTotal, totalRowData];
    }
    
    return tableData;
  };

  const tableData = createTableData();

  // Download functions
  const downloadAsExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      if (!hasBreakdown || isShowByYear) {
        // Simple table export
        const showAmountColumn = (summaryData?.totals?.deal_amount || 0) > 0;
        const effectiveValueBy = valueBy === 'both' && !showAmountColumn ? 'deals' : valueBy;
        
        const exportData = chartDataArray.map(yearData => {
          const row = {
            'Year': formatPeriodLabel(String(yearData.label))
          };
          
          if (effectiveValueBy === 'deals' || effectiveValueBy === 'both') {
            row['No. of Deals'] = yearData.total?.deal_count || yearData.total || 0;
          }
          
          if ((effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn) {
            row['Amount (US$ M)'] = parseFloat((yearData.total?.deal_amount || 0).toFixed(2));
          }
          
          return row;
        });
        
        // Add total row
        const totalRow = { 'Year': 'Total' };
        if (effectiveValueBy === 'deals' || effectiveValueBy === 'both') {
          totalRow['No. of Deals'] = summaryData?.totals?.deal_count || 0;
        }
        if ((effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn) {
          totalRow['Amount (US$ M)'] = parseFloat((summaryData?.totals?.deal_amount || 0).toFixed(2));
        }
        exportData.push(totalRow);
        
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        
        // Apply number formatting with commas to amount columns
        if ((effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn) {
          const range = XLSX.utils.decode_range(worksheet['!ref']);
          const amountColIndex = effectiveValueBy === 'both' ? 2 : 1; // Column C for 'both', Column B for 'amount' only
          
          for (let row = 1; row <= range.e.r; row++) { // Start from row 1 (skip header)
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: amountColIndex });
            if (worksheet[cellAddress] && typeof worksheet[cellAddress].v === 'number') {
              worksheet[cellAddress].z = '#,##0.00'; // Number format with comma separators
            }
          }
        }
        
        // Add copyright message after 2 empty rows
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const copyrightRow = range.e.r + 3; // Add 2 empty rows, then copyright on 3rd row
        const copyrightMessage = '© TSJ Media Pvt. Ltd. This data is meant for the internal and non-commercial use of the purchaser and cannot be resold, rented, licensed or otherwise transmitted without the prior permission of TSJ Media. Any unauthorized redistribution will constitute a violation of copyright law.';
        
        XLSX.utils.sheet_add_aoa(worksheet, [[copyrightMessage]], { origin: { r: copyrightRow, c: 0 } });
        
        // Update the range to include the copyright row
        worksheet['!ref'] = XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: copyrightRow, c: range.e.c }
        });
        
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      } else {
        // Complex table export with proper headers
        const worksheet = XLSX.utils.aoa_to_sheet([]);
        const years = chartCategories;
        
        // Create header rows
        const headerRow1 = [getShowByLabel(showBy) || 'Category']; // First column
        const headerRow2 = ['']; // Empty cell under category
        
        // Add year headers and sub-headers
        years.forEach(year => {
          const formattedYear = formatPeriodLabel(String(year));
          if (valueBy === 'both') {
            headerRow1.push(formattedYear, ''); // Year spans 2 columns
            headerRow2.push('No. of Deals', 'Amount ($M)');
          } else if (valueBy === 'deals') {
            headerRow1.push(formattedYear);
            headerRow2.push('No. of Deals');
          } else {
            headerRow1.push(formattedYear);
            headerRow2.push('Amount ($M)');
          }
        });
        
        // Add header rows to worksheet
        XLSX.utils.sheet_add_aoa(worksheet, [headerRow1, headerRow2], { origin: 'A1' });
        
        // Prepare data rows
        const dataRows = tableData.map(row => {
          const rowData = [row.industry];
          
          years.forEach(year => {
            if (valueBy === 'both') {
              rowData.push(
                row[`${year}_deals`] || 0,
                row[`${year}_amount`] || 0
              );
            } else if (valueBy === 'deals') {
              rowData.push(row[year] || 0);
            } else {
              rowData.push(row[year] || 0);
            }
          });
          
          return rowData;
        });
        
        // Add data rows starting from row 3 (after headers)
        XLSX.utils.sheet_add_aoa(worksheet, dataRows, { origin: 'A3' });
        
        // Apply number formatting with commas to amount columns
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        if (valueBy === 'both') {
          // For 'both' mode, format every other column starting from column 2 (amount columns)
          for (let row = 2; row <= range.e.r; row++) { // Start from row 2 (after headers)
            let colIndex = 2; // Start from column C (first amount column)
            years.forEach(() => {
              const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });
              if (worksheet[cellAddress] && typeof worksheet[cellAddress].v === 'number') {
                worksheet[cellAddress].z = '#,##0.00'; // Number format with comma separators
              }
              colIndex += 2; // Skip to next amount column (skip deals column)
            });
          }
        } else if (valueBy === 'amount') {
          // For 'amount' mode, format all data columns
          for (let row = 2; row <= range.e.r; row++) { // Start from row 2 (after headers)
            for (let col = 1; col <= range.e.c; col++) { // Start from column B
              const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
              if (worksheet[cellAddress] && typeof worksheet[cellAddress].v === 'number') {
                worksheet[cellAddress].z = '#,##0.00'; // Number format with comma separators
              }
            }
          }
        }
        
        // Merge cells for year headers when valueBy is 'both'
        if (valueBy === 'both') {
          const merges = [];
          let colIndex = 1; // Start after first column (industry)
          years.forEach(() => {
            merges.push({
              s: { r: 0, c: colIndex }, // start row, start col
              e: { r: 0, c: colIndex + 1 } // end row, end col
            });
            colIndex += 2;
          });
          worksheet['!merges'] = merges;
        }
        
        // Add copyright message after 2 empty rows
        const copyrightRow = range.e.r + 3; // Add 2 empty rows, then copyright on 3rd row
        const copyrightMessage = '© TSJ Media Pvt. Ltd. This data is meant for the internal and non-commercial use of the purchaser and cannot be resold, rented, licensed or otherwise transmitted without the prior permission of TSJ Media. Any unauthorized redistribution will constitute a violation of copyright law.';
        
        XLSX.utils.sheet_add_aoa(worksheet, [[copyrightMessage]], { origin: { r: copyrightRow, c: 0 } });
        
        // Update the range to include the copyright row
        worksheet['!ref'] = XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: copyrightRow, c: range.e.c }
        });
        
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      }
      
      const fileName = subType ? `${subType}.xlsx` : 'chart_data.xlsx';
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  const chartOptions = {
    chart: {
      type: "bar",
      height: "100%",
      stacked: true,
      toolbar: {
        show: true,
        tools: {
          download: downloadIconSVG,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        },
        export: {
          csv: {
            filename: subType || 'chart_data',
          },
          svg: {
            filename: subType || 'chart',
          },
          png: {
            filename: subType || 'chart',
          }
        }
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "70%",
        borderRadius: 0
      }
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories: displayChartCategories,
      title: {
        text: valueBy === "amount" ? "Total Amount (US$ M)" : "Total Deals",
        style: {
          fontSize: "14px",
          fontWeight: "600",
          color: "#374151"
        }
      },
      labels: {
        style: {
          fontSize: "12px",
          colors: "#6B7280"
        },
        formatter: function (val) {
          if (valueBy === "amount") {
            return `$${val.toLocaleString()}M`;
          } else {
            return val.toLocaleString();
          }
        }
      }
    },
    yaxis: {
      title: {
        text: INTERVAL_OPTIONS.find(o => o.value === interval)?.label ?? 'Year',
        style: {
          fontSize: "14px",
          fontWeight: "600",
          color: "#374151"
        }
      },
      labels: {
        style: {
          fontSize: "12px",
          colors: "#6B7280"
        },
        formatter: function (val) {
          return Math.round(val).toString(); // Remove decimals from year labels
        }
      }
    },
    grid: {
      borderColor: "#E5E7EB",
      strokeDashArray: 2,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      }
    },
    tooltip: {
      shared: false,
      intersect: true,
      theme: "light",
      style: {
        fontSize: "12px"
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const year = chartCategories[dataPointIndex];
        const formattedYear = formatPeriodLabel(String(year));
        const industryGroup = w.globals.seriesNames[seriesIndex];
        const value = series[seriesIndex][dataPointIndex];
        const yearData = chartDataArray[dataPointIndex];
        const totalForYear = valueBy === "amount" 
          ? (yearData?.total?.deal_amount || 0) 
          : (yearData?.total?.deal_count || yearData?.total || 0);
        const percentage = totalForYear > 0 ? ((value / totalForYear) * 100).toFixed(1) : 0;
        
        const valueDisplay = valueBy === "amount" 
          ? `$${value.toLocaleString()}M` 
          : value.toLocaleString();
        const totalDisplay = valueBy === "amount" 
          ? `$${totalForYear.toLocaleString()}M` 
          : totalForYear.toLocaleString();
        
        return `
          <div class="bg-white p-3 rounded-lg shadow-lg border">
            <div class="font-semibold text-gray-800 mb-2">${formattedYear}</div>
            <div class="text-sm text-gray-600 mb-1">
              <span class="font-medium" style="color: ${getColorByGroup(industryGroup)}">${industryGroup}</span>
            </div>
            <div class="text-lg font-bold text-gray-900">${valueDisplay}</div>
            <div class="text-xs text-gray-500">${percentage}% of year total</div>
            <div class="text-xs text-gray-400 mt-1">Total for ${year}: ${totalDisplay}</div>
          </div>
        `;
      }
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      markers: {
        width: 12,
        height: 12,
        radius: 2
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4
      }
    }
  };

  const addCommasToNumber = (number) => {
    return number?.toFixed(0).toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getPeriodLabel = () => {
    const base = interval === 'half-yearly' ? 'Half-Year' : interval === 'quarterly' ? 'Quarter' : 'Year';
    if (period === 'FY') return `${base} (FY)`;
    return base;
  };

  return (
    <div className="pl-3 pr-4 flex items-center justify-center h-full">
      {/* Blur background overlay */}
      <div 
        className="bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Popup content */}
      <div className="bg-white shadow-2xl w-full h-full flex flex-col">
        {/* Header */}
        <div className="p-2 pl-6 flex items-center justify-between border-b border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col space-y-2 items-start">
            <div className="flex mt-1 space-x-6 text-xs md:text-sm text-gray-600">
              {(valueBy === "amount" || valueBy === "both") && (summaryData?.totals?.deal_amount || 0) > 0 && (
                <div>
                  Total Amount:{" "}
                  <span className="font-semibold text-black">
                    ${addCommasToNumber(summaryData?.totals?.deal_amount || 0)} M
                  </span>
                </div>
              )}
              {(valueBy === "deals" || valueBy === "both") && (
                <div>
                  Total Deals:{" "}
                  <span className="font-semibold text-black">
                    {(summaryData?.totals?.deal_count || 0).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Toggle buttons and filters - on mobile, only show Year filter */}
          <div className={`flex items-center ${isMobile ? 'flex-col space-y-2' : 'space-x-4'}`}>
            {/* Refine Search button - opens the page's refine search filter modal */}
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className={`flex items-center space-x-2 h-[32px] px-3 rounded-md border text-xs md:text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#bc8b20] focus:ring-offset-1 ${
                activeFilterCount > 0 || open
                  ? 'border-[#BC8B20] bg-[#FAF5EA] text-[#BC8B20]'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-[#BC8B20] hover:text-[#BC8B20] hover:bg-[#FAF5EA]'
              }`}
              title="Refine Search"
              aria-label="Refine Search"
            >
              <FaFilter size={12} className="text-[#BC8B20]" />
              <span className="whitespace-nowrap">Filter</span>
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#BC8B20] text-white text-[10px] font-semibold leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Show By Filter - Only show when there's data and breakdown exists. On mobile, disabled (Year only) */}
            {chartDataArray.length > 0 && hasBreakdown && (
              <div className="flex items-center space-x-2">
                <label className="text-xs md:text-sm font-medium text-gray-700">Show by:</label>
                <select
                  value={showBy}
                  onChange={(e) => onShowByChange && onShowByChange(e.target.value)}
                  disabled={isMobile || availableShowByOptions.length <= 1}
                  className={`px-3 py-1 border border-gray-300 rounded-md text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#bc8b20] focus:border-transparent ${
                    isMobile || availableShowByOptions.length <= 1 
                      ? 'bg-gray-50 text-gray-600 cursor-default' 
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  title={isMobile 
                    ? "Year view only on mobile" 
                    : availableShowByOptions.length <= 1 
                    ? "Only one grouping option available based on your filters" 
                    : "Select how to group your chart data"
                  }
                >
                  {availableShowByOptions.map(option => {
                    const optionLabels = {
                      'industry': 'Industry',
                      'sector': 'Sector',
                      'country': 'Countries',
                      'region': 'Region',
                      'yearFounded': 'Year Founded',
                      'state': 'State',
                      'city': 'City',
                      'investorType': 'Investor Type',
                      'investor_headquaters_city': 'Investor HQ City',
                      'firm_type': 'Firm Type',
                      'firm_Sub_type': 'Firm Sub Type',
                      'tag': 'Tags',
                      'ladvisors': 'Legal Advisors',
                      'tadvisors': 'Transaction Advisors',
                      'investor': 'Investors',
                      'company': 'Target Companies',
                      'deal_type': 'Deal Type',
                      'investor_headquaters': 'Investor Headquarters',
                      'company_type': 'Company Type',
                      'deals_involving': 'Deals Involving',
                      'deal_range': 'Deal Range',
                      'exit_status': 'Exit Status',
                      'investor_type': 'Investor Type',
                      'investor_sale': 'Investor Sale',
                      'exit_type': 'Exit Type',
                      'vc_fund': 'VC Fund',
                      'exited': 'Exited',
                      'incubation_status': 'Incubation Status',
                      'defunct': 'Defunct Status',
                      // M&A specific labels
                      'target_industry': 'Target Industry',
                      'target_sector': 'Target Sector',
                      'target_country': 'Target Country',
                      'target_region': 'Target Region',
                      'target_state': 'Target State',
                      'target_city': 'Target City',
                      'target_company_type': 'Target Company Type',
                      'target_company': 'Target Company',
                      'acquirer_industry': 'Acquirer Industry',
                      'acquirer_country': 'Acquirer Country',
                      'acquirer_region': 'Acquirer Region',
                      'acquirer_state': 'Acquirer State',
                      'acquirer_city': 'Acquirer City',
                      'acquirer_company_type': 'Acquirer Company Type',
                      'acquirer_group': 'Acquirer Group',
                      'company_group': 'Company Group',
                      'acquirer': 'Acquirer',
                      'year': 'Year',
                    };
                    return (
                      <option key={option} value={option}>
                        {optionLabels[option] || option}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            
            {/* Period Filter */}
            {!isMobile && onPeriodChange && (
              <div className="flex items-center space-x-2">
                <label className="text-xs md:text-sm font-medium text-gray-700">Period:</label>
                <select
                  value={period}
                  onChange={(e) => onPeriodChange(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#bc8b20] focus:border-transparent bg-white hover:bg-gray-50"
                >
                  {PERIOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Interval Filter */}
            {!isMobile && onIntervalChange && (
              <div className="flex items-center space-x-2">
                <label className="text-xs md:text-sm font-medium text-gray-700">Interval:</label>
                <select
                  value={interval}
                  onChange={(e) => onIntervalChange(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#bc8b20] focus:border-transparent bg-white hover:bg-gray-50"
                >
                  {INTERVAL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Value By Filter - Hide for angel and incubation. On mobile, hide entirely */}
            {!isAngelOrIncubation && !isMobile && (
              <div className="flex items-center space-x-2">
                <label className="text-xs md:text-sm font-medium text-gray-700">Value by:</label>
                <select
                  value={valueBy}
                  onChange={(e) => setValueBy(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#bc8b20] focus:border-transparent"
                >
                  <option value="deals">Deals</option>
                  <option value="amount">Amount</option>
                  <option value="both">Both</option>
                </select>
              </div>
            )}
            
            {/* View Mode Buttons - Hide on mobile (table only) */}
            {!isMobile && (
              <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  viewMode === "table"
                    ? "bg-white text-[#bc8b20] shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <FaTable size={16} />
                <span>Table</span>
              </button>
              <button
                onClick={() => setViewMode("chart")}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  viewMode === "chart"
                    ? "bg-white text-[#bc8b20] shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <FaChartBar size={16} />
                <span>Bar</span>
              </button>
              {/* Hide Pie and Insights buttons when there's no breakdown */}
              {(hasBreakdown && !isShowByYear) && (
                <>
                  <button
                    onClick={() => setViewMode("pie")}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                      viewMode === "pie"
                        ? "bg-white text-[#bc8b20] shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    <FaChartPie size={16} />
                    <span>Pie</span>
                  </button>
                  {/* {!isAngelOrIncubation && <button
                    onClick={() => setViewMode("datapoints")}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === "datapoints"
                        ? "bg-white text-[#bc8b20] shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    <FaDotCircle size={16} />
                    <span>Insights</span>
                  </button>} */}
                </>
              )}
            </div>
            )}
            
            {/* Download button - show only for table view, hide on mobile */}
            {viewMode === "table" && !isMobile && (
              <button
                onClick={downloadAsExcel}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-[#bc8b20] transition-colors border border-gray-300 rounded-md"
                title="Download as Excel"
              >
                <FaDownload size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 h-full overflow-y-auto p-4 no-scrollbar">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#bc8b20] mx-auto mb-4"></div>
                <div className="text-gray-600 text-lg mb-2">Loading Analytics...</div>
                <div className="text-gray-400 text-sm">Please wait while we fetch the chart data</div>
              </div>
            </div>
          ) : chartDataArray.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-gray-500 text-lg mb-2">No data available</div>
                <div className="text-gray-400 text-sm">Please check back later or contact support</div>
              </div>
            </div>
          ) : viewMode === "chart" ? (
            <div className="w-full h-full flex flex-col">
              {/* Chart Container */}
              <div className="flex-1 min-h-0">
                {/* For angel/incubation, always show column chart with deals only */}
                {isAngelOrIncubation ? (
                  <div className="flex flex-col w-full h-full">
                    <div 
                      className="flex-1 flex justify-center items-center"
                      style={{
                        width:
                          yearCount <= 2
                            ? "35%"
                            : yearCount <= 4
                            ? "45%"
                            : yearCount <= 6
                            ? "55%"
                            : yearCount <= 9
                            ? "75%"
                            : "100%",
                        maxWidth: yearCount <= 7 ? "170%" : "100%",
                        margin: '0 auto'
                      }}
                    >
                      <div className="w-full h-full">
                        <Chart
                          options={{
                            chart: {
                              type: 'bar',
                              height: '100%',
                              toolbar: {
                                show: true,
                                tools: {
                                  download: downloadIconSVG,
                                  selection: false,
                                  zoom: false,
                                  zoomin: false,
                                  zoomout: false,
                                  pan: false,
                                  reset: false
                                },
                                export: {
                                  csv: {
                                    filename: subType || 'chart_data',
                                  },
                                  svg: {
                                    filename: subType || 'chart',
                                  },
                                  png: {
                                    filename: subType || 'chart',
                                  }
                                }
                              },
                              animations: {
                                enabled: true,
                                easing: 'easeinout',
                                speed: 800
                              }
                            },
                            plotOptions: {
                              bar: {
                                columnWidth:
                                yearCount <= 2
                                  ? "25%"    // very thin bars
                                  : yearCount <= 5
                                  ? "35%"    // medium
                                  : yearCount <= 9
                                  ? "40%"    // normal
                                  : "40%",   // wider when many bars
                                borderRadius: 4,
                                borderRadiusApplication: 'end',
                                dataLabels: {
                                  position: 'top'
                                }
                              }
                            },
                            dataLabels: {
                              enabled: false
                            },
                            xaxis: {
                              categories: displaySortedChartCategories,
                              title: {
                                text: "Years",
                                style: {
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151"
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  colors: "#6b7280"
                                }
                              }
                            },
                            yaxis: {
                              title: {
                                text: "No. of Deals",
                                style: {
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151"
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: "12px",
                                  colors: "#6b7280"
                                },
                                formatter: function (val) {
                                  return val ? val.toLocaleString() : '0';
                                }
                              }
                            },
                            colors: ['#bc8b20'],
                            fill: {
                              opacity: 0.85
                            },
                            legend: {
                              show: false
                            },
                            tooltip: {
                              theme: "light",
                              style: {
                                fontSize: "12px"
                              },
                              y: {
                                formatter: function (val) {
                                  return val ? val.toLocaleString() + " deals" : "0 deals";
                                }
                              }
                            },
                            grid: {
                              borderColor: '#e5e7eb',
                              strokeDashArray: 3,
                              xaxis: {
                                lines: {
                                  show: true
                                }
                              },
                              yaxis: {
                                lines: {
                                  show: true
                                }
                              }
                            }
                          }}
                          series={[
                            {
                              name: "No. of Deals",
                              data: sortedChartCategories.map(year => {
                                const yearData = sortedChartDataArray.find(item => item.label === year);
                                return yearData?.total?.deal_count || yearData?.total || 0;
                              })
                            }
                          ]}
                          type="bar"
                          height="100%"
                        />
                      </div>
                    </div>
                  </div>
                ) : 
                /* Show combined chart whenever valueBy is "both" */
                valueBy === "both" ? (
                  <div className="flex flex-col w-full h-full">
                    {/* Combined Chart */}
                    <div 
                      className="flex-1 flex justify-center items-center"
                      style={{
                        width:
                          yearCount <= 2
                            ? "35%"
                            : yearCount <= 4
                            ? "45%"
                            : yearCount <= 6
                            ? "55%"
                            : yearCount <= 9
                            ? "75%"
                            : "100%",
                        maxWidth: yearCount <= 7 ? "170%" : "100%",
                        margin: "0 auto"
                      }}
                    >
                      <div className="w-full h-full">
                      <Chart
                        options={{
                          chart: {
                            type: 'line',
                            height: '100%',
                            toolbar: {
                              show: true,
                              tools: {
                                download: downloadIconSVG,
                                selection: false,
                                zoom: false,
                                zoomin: false,
                                zoomout: false,
                                pan: false,
                                reset: false
                              },
                              export: {
                                csv: {
                                  filename: subType || 'chart_data',
                                },
                                svg: {
                                  filename: subType || 'chart',
                                },
                                png: {
                                  filename: subType || 'chart',
                                }
                              }
                            },
                            animations: {
                              enabled: true,
                              easing: 'easeinout',
                              speed: 800
                            }
                          },
                          stroke: {
                            width: [0, 2], // 0 for bars, 2 for line (reduced from 4)
                            curve: 'smooth'
                          },
                          plotOptions: {
                            bar: {
                              columnWidth:
                                yearCount <= 2
                                  ? "25%"    // very thin bars
                                  : yearCount <= 5
                                  ? "35%"    // medium
                                  : yearCount <= 9
                                  ? "40%"    // normal
                                  : "40%",   // wider when many bars
                              dataLabels: {
                                position: 'top'
                              },
                              borderRadius: 4, // Add border radius to top of bars
                              borderRadiusApplication: 'end' // Apply radius only to the top
                            }
                          },
                          dataLabels: {
                            enabled: false
                          },
                          xaxis: {
                            categories: displaySortedChartCategories,
                            title: {
                              text: "Years",
                              style: {
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#374151"
                              },
                              offsetY: 80 
                            },
                            labels: {
                              style: {
                                fontSize: "12px",
                                fontWeight: "500",
                                colors: "#6b7280"
                              }
                            }
                          },
                          yaxis: [
                            {
                              title: {
                                text: 'No. of Deals',
                                style: {
                                  color: '#bc8b20',
                                  fontSize: "14px",
                                  fontWeight: "600"
                                }
                              },
                              labels: {
                                style: {
                                  colors: '#bc8b20',
                                  fontSize: "12px"
                                },
                                formatter: function (val) {
                                  return val ? val.toLocaleString() : '0';
                                },
                                offsetX: -15
                              }
                            },
                            {
                              opposite: true,
                              title: {
                                text: 'Amount (US$ M)',
                                style: {
                                  color: '#4b5563',
                                  fontSize: "14px",
                                  fontWeight: "600"
                                }
                              },
                              labels: {
                                style: {
                                  colors: '#4b5563',
                                  fontSize: "12px"
                                },
                                formatter: function (val) {
                                  return val ? `$${addCommasToNumber(val)}M` : '$0M';
                                },
                                offsetX: -15
                              }
                            }
                          ],
                          colors: ['#bc8b20', '#4b5563'],
                          fill: {
                            opacity: [0.85, 1]
                          },
                          legend: {
                            show: true,
                            position: "top",
                            horizontalAlign: "center",
                            fontSize: "14px",
                            markers: {
                              width: 12,
                              height: 12,
                              radius: 2
                            }
                          },
                          tooltip: {
                            shared: true,
                            intersect: false,
                            theme: "light",
                            style: {
                              fontSize: "12px"
                            },
                            y: [
                              {
                                formatter: function (val) {
                                  return val ? val.toLocaleString() + " deals" : "0 deals";
                                }
                              },
                              {
                                formatter: function (val) {
                                  return val ? "$" + addCommasToNumber(val) + "M" : "$0M";
                                }
                              }
                            ]
                          },
                          grid: {
                            borderColor: '#e5e7eb',
                            strokeDashArray: 3,
                            xaxis: {
                              lines: {
                                show: true
                              }
                            },
                            yaxis: {
                              lines: {
                                show: true
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            name: 'No. of Deals',
                            type: 'column',
                            yAxisIndex: 0,
                            data: sortedChartCategories.map(year => {
                              const yearData = sortedChartDataArray.find(item => item.label === year);
                              return yearData?.total?.deal_count || yearData?.total || 0;
                            })
                          },
                          {
                            name: 'Amount (US$ M)',
                            type: 'line',
                            yAxisIndex: 1,
                            data: sortedChartCategories.map(year => {
                              const yearData = sortedChartDataArray.find(item => item.label === year);
                              return yearData?.total?.deal_amount || 0;
                            })
                          }
                        ]}
                        type="line"
                        height="100%"
                      />
                      </div>
                    </div>
                  </div>
                ) : (!hasBreakdown || isShowByYear) && (valueBy === "deals" || valueBy === "amount") ? (
                  /* Single bar chart for deals or amount without breakdown */
                  <div className="flex flex-col w-full h-full">
                    <div 
                      className="flex-1 flex justify-center items-center"
                      style={{
                        width:
                          yearCount <= 2
                            ? "35%"
                            : yearCount <= 4
                            ? "45%"
                            : yearCount <= 6
                            ? "55%"
                            : yearCount <= 9
                            ? "75%"
                            : "100%",
                        maxWidth: yearCount <= 7 ? "170%" : "100%",
                        margin: '0 auto'
                      }}
                    >
                      <div className="w-full h-full">
                        <Chart
                          options={{
                            chart: {
                              type: 'bar',
                              height: '100%',
                              toolbar: {
                                show: true,
                                tools: {
                                  download: downloadIconSVG,
                                  selection: false,
                                  zoom: false,
                                  zoomin: false,
                                  zoomout: false,
                                  pan: false,
                                  reset: false
                                },
                                export: {
                                  csv: {
                                    filename: subType || 'chart_data',
                                  },
                                  svg: {
                                    filename: subType || 'chart',
                                  },
                                  png: {
                                    filename: subType || 'chart',
                                  }
                                }
                              },
                              animations: {
                                enabled: true,
                                easing: 'easeinout',
                                speed: 800
                              }
                            },
                            plotOptions: {
                              bar: {
                                columnWidth:
                                yearCount <= 2
                                  ? "25%"    // very thin bars
                                  : yearCount <= 5
                                  ? "35%"    // medium
                                  : yearCount <= 9
                                  ? "40%"    // normal
                                  : "40%",   // wider when many bars
                                borderRadius: 4,
                                borderRadiusApplication: 'end',
                                dataLabels: {
                                  position: 'top'
                                }
                              }
                            },
                            dataLabels: {
                              enabled: false
                            },
                            xaxis: {
                              categories: displaySortedChartCategories,
                              title: {
                                text: "Years",
                                style: {
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151"
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  colors: "#6b7280"
                                }
                              }
                            },
                            yaxis: {
                              title: {
                                text: valueBy === "amount" ? "Amount (US$ M)" : "No. of Deals",
                                style: {
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151"
                                }
                              },
                              labels: {
                                style: {
                                  fontSize: "12px",
                                  colors: "#6b7280"
                                },
                                formatter: function (val) {
                                  if (valueBy === "amount") {
                                    return val ? `$${addCommasToNumber(val)}M` : '$0M';
                                  } else {
                                    return val ? val.toLocaleString() : '0';
                                  }
                                }
                              }
                            },
                            colors: ['#bc8b20'],
                            fill: {
                              opacity: 0.85
                            },
                            legend: {
                              show: false
                            },
                            tooltip: {
                              theme: "light",
                              style: {
                                fontSize: "12px"
                              },
                              y: {
                                formatter: function (val) {
                                  if (valueBy === "amount") {
                                    return val ? "$" + addCommasToNumber(val) + "M" : "$0M";
                                  } else {
                                    return val ? val.toLocaleString() + " deals" : "0 deals";
                                  }
                                }
                              }
                            },
                            grid: {
                              borderColor: '#e5e7eb',
                              strokeDashArray: 3,
                              xaxis: {
                                lines: {
                                  show: true
                                }
                              },
                              yaxis: {
                                lines: {
                                  show: true
                                }
                              }
                            }
                          }}
                          series={[
                            {
                              name: valueBy === "amount" ? "Amount (US$ M)" : "No. of Deals",
                              data: sortedChartCategories.map(year => {
                                const yearData = sortedChartDataArray.find(item => item.label === year);
                                if (valueBy === "amount") {
                                  return yearData?.total?.deal_amount || 0;
                                } else {
                                  return yearData?.total?.deal_count || yearData?.total || 0;
                                }
                              })
                            }
                          ]}
                          type="bar"
                          height="100%"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Stacked bar chart with breakdown */
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height="100%"
                  />
                )}
              </div>
            </div>
          ) : viewMode === "pie" ? (
            <div className="w-full h-full flex flex-col">
              {/* Pie Chart Container */}
              <div className="flex-1 min-h-full">
                {valueBy === "both" ? (
                  <div className={`flex w-full h-full ${hasDeals && hasAmount ? 'gap-4' : ''}`}>
                    {/* Deals Pie Chart */}
                    {hasDeals && (
                    <div className={`flex flex-col h-full ${hasAmount ? 'flex-1' : 'w-full'}`}>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">Number of Deals</h3>
                      <div className="flex-1 h-full">
                        <Chart
                          options={{
                            chart: {
                              type: 'pie',
                              height: '100%',
                              toolbar: {
                                show: true,
                                tools: {
                                  download: downloadIconSVG,
                                  selection: false,
                                  zoom: false,
                                  zoomin: false,
                                  zoomout: false,
                                  pan: false,
                                  reset: false
                                },
                                export: {
                                  csv: {
                                    filename: subType || 'chart_data',
                                  },
                                  svg: {
                                    filename: subType || 'chart',
                                  },
                                  png: {
                                    filename: subType || 'chart',
                                  }
                                }
                              }
                            },
                            labels: Array.from(allGroups),
                            colors: Array.from(allGroups).map(group => getColorByGroup(group)),
                            legend: {
                              show: !hasAmount && Array.from(allGroups).length <= 10,
                              position: "right",
                              horizontalAlign: "center",
                              fontSize: "11px",
                              height: !hasAmount && Array.from(allGroups).length > 8 ? '100%' : undefined,
                              offsetY: 0,
                              markers: {
                                width: 10,
                                height: 10,
                                radius: 2
                              },
                              itemMargin: {
                                horizontal: 5,
                                vertical: 2
                              },
                              containerMargin: {
                                left: 10,
                                top: 10
                              }
                            },
                            tooltip: {
                              y: {
                                formatter: function (val) {
                                  return val.toLocaleString() + " deals";
                                }
                              }
                            },
                            dataLabels: {
                              enabled: true,
                              formatter: function (val) {
                                return val.toFixed(1) + '%';
                              }
                            }
                          }}
                          series={Array.from(allGroups).map(group => {
                            let total = 0;
                            chartDataArray.forEach(yearData => {
                              const groupData = yearData.breakdown?.find(item => item.group === group);
                              if (groupData) {
                                total += groupData.deal_count || groupData.value || 0;
                              }
                            });
                            return total;
                          })}
                          type="pie"
                          height="100%"
                        />
                      </div>
                    </div>
                    )}
                    
                    {/* Vertical Divider Line */}
                    {hasDeals && hasAmount && (
                    <div className="w-px h-[100%] bg-gray-300"></div>
                    )}
                    
                    {/* Amount Pie Chart */}
                    {hasAmount && (
                    <div className={`flex flex-col h-full ${hasDeals ? 'flex-1' : 'w-full'}`}>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">Amount (US$ M)</h3>
                      <div className="flex-1 h-full">
                        <Chart
                          options={{
                            chart: {
                              type: 'pie',
                              height: '100%',
                              toolbar: {
                                show: true,
                                tools: {
                                  download: downloadIconSVG,
                                  selection: false,
                                  zoom: false,
                                  zoomin: false,
                                  zoomout: false,
                                  pan: false,
                                  reset: false
                                },
                                export: {
                                  csv: {
                                    filename: subType || 'chart_data',
                                  },
                                  svg: {
                                    filename: subType || 'chart',
                                  },
                                  png: {
                                    filename: subType || 'chart',
                                  }
                                }
                              }
                            },
                            labels: Array.from(allGroups),
                            colors: Array.from(allGroups).map(group => getColorByGroup(group)),
                            legend: {
                              show: !hasDeals && Array.from(allGroups).length <= 10,
                              position: "right",
                              horizontalAlign: "center",
                              fontSize: "11px",
                              height: !hasDeals && Array.from(allGroups).length > 8 ? '100%' : undefined,
                              offsetY: 0,
                              markers: {
                                width: 10,
                                height: 10,
                                radius: 2
                              },
                              itemMargin: {
                                horizontal: 5,
                                vertical: 2
                              },
                              containerMargin: {
                                left: 10,
                                top: 10
                              }
                            },
                            tooltip: {
                              y: {
                                formatter: function (val) {
                                  return "$" + val.toLocaleString() + "M";
                                }
                              }
                            },
                            dataLabels: {
                              enabled: true,
                              formatter: function (val) {
                                return val.toFixed(1) + '%';
                              }
                            }
                          }}
                          series={Array.from(allGroups).map(group => {
                            let total = 0;
                            chartDataArray.forEach(yearData => {
                              const groupData = yearData.breakdown?.find(item => item.group === group);
                              if (groupData) {
                                total += groupData.deal_amount || 0;
                              }
                            });
                            return total;
                          })}
                          type="pie"
                          height="100%"
                        />
                      </div>
                    </div>
                    )}
                  </div>
                ) : (
                  <Chart
                    options={{
                      chart: {
                        type: 'pie',
                        height: '100%',
                        toolbar: {
                          show: true,
                          tools: {
                            download: downloadIconSVG,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false
                          },
                          export: {
                            csv: {
                              filename: subType || 'chart_data',
                            },
                            svg: {
                              filename: subType || 'chart',
                            },
                            png: {
                              filename: subType || 'chart',
                            }
                          }
                        }
                      },
                      labels: Array.from(allGroups),
                      colors: Array.from(allGroups).map(group => getColorByGroup(group)),
                      legend: {
                        show: true,
                        position: Array.from(allGroups).length > 8 ? "right" : "bottom",
                        horizontalAlign: "center",
                        fontSize: "11px",
                        height: Array.from(allGroups).length > 8 ? '100%' : undefined,
                        offsetY: 0,
                        markers: {
                          width: 10,
                          height: 10,
                          radius: 2
                        },
                        itemMargin: {
                          horizontal: 5,
                          vertical: 2
                        },
                        containerMargin: {
                          left: Array.from(allGroups).length > 8 ? 10 : 0,
                          top: 10
                        }
                      },
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return valueBy === "amount" 
                              ? "$" + val.toLocaleString() + "M"
                              : val.toLocaleString() + " deals";
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return val.toFixed(1) + '%';
                        }
                      }
                    }}
                    series={Array.from(allGroups).map(group => {
                      let total = 0;
                      chartDataArray.forEach(yearData => {
                        const groupData = yearData.breakdown?.find(item => item.group === group);
                        if (groupData) {
                          if (valueBy === "amount") {
                            total += groupData.deal_amount || 0;
                          } else {
                            total += groupData.deal_count || groupData.value || 0;
                          }
                        }
                      });
                      return total;
                    })}
                    type="pie"
                    height="100%"
                  />
                )}
              </div>
              {/* Legend for both pie charts when showing both */}
              {valueBy === "both" && Array.from(allGroups) && (
                <div className="flex justify-center my-4" 
                  // style={{ minHeight: '120px', maxHeight: '160px' }}
                >
                  <div 
                    className={`gap-x-4 gap-y-2 text-xs w-full overflow-auto grid grid-cols-4 content-center justify-items-center `}
                  >
                    {Array.from(allGroups).map((group, index) => (
                      <div 
                        key={group} 
                        className="flex items-center space-x-1"
                      >
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: getColorByGroup(group) }}
                        ></div>
                        <span className="text-gray-700 truncate text-xs w-40">{group}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) 
          // : viewMode === "datapoints" ? (
          //   <div className="w-full h-full overflow-auto">
          //     <div className="bg-gray-50 rounded-lg p-4 h-full">
          //       <h3 className="text-lg font-semibold text-gray-800 mb-4">
          //         Investment Data by Year and {getShowByLabel(showBy)}
          //       </h3>
          //       <div className="overflow-auto" style={{ maxHeight: 'calc(100% - 3rem)' }}>
          //         {chartDataArray.map((yearData, yearIndex) => (
          //           <div key={yearIndex} className="mb-6">
          //             <h4 className="text-md font-semibold text-gray-700 mb-3 bg-white p-3 rounded-lg shadow-sm">
          //               {yearData.label} - Total: {valueBy === "amount" 
          //                 ? `$${addCommasToNumber(yearData.total?.deal_amount || 0)}M` 
          //                 : valueBy === "deals"
          //                 ? (yearData.total?.deal_count || yearData.total || 0).toLocaleString()
          //                 : `$${addCommasToNumber(yearData.total?.deal_amount || 0)}M | ${(yearData.total?.deal_count || 0).toLocaleString()} deals`}
          //             </h4>
          //             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          //               {yearData.breakdown?.map((item, index) => (
          //                 <div 
          //                   key={index} 
          //                   className="bg-white p-3 rounded-lg shadow-sm border-l-4 hover:shadow-md transition-shadow"
          //                   style={{ borderLeftColor: getColorByGroup(item.group) }}
          //                 >
          //                   <div className="flex justify-between items-center">
          //                     <div>
          //                       <div className="text-base font-medium text-gray-900">
          //                         {item.group}
          //                       </div>
          //                     </div>
          //                     <div className="text-right">
          //                       <div className="text-lg font-bold" style={{ color: getColorByGroup(item.group) }}>
          //                         {valueBy === "amount" 
          //                           ? (
          //                             <>
          //                               <span>${addCommasToNumber(item.deal_amount || 0)}M</span>
          //                               <span className="text-sm font-normal text-gray-500">
          //                                 {' '}({(((item.deal_amount || 0) / (yearData.total?.deal_amount || 1)) * 100).toFixed(1)}% of total)
          //                               </span>
          //                             </>
          //                           )
          //                           : valueBy === "deals"
          //                           ? (
          //                             <>
          //                               <span>{(item.deal_count || item.value || 0).toLocaleString()} deals</span>
          //                               <span className="text-sm font-normal text-gray-500">
          //                                 {' '}({(((item.deal_count || item.value || 0) / (yearData.total?.deal_count || 1)) * 100).toFixed(1)}% of total)
          //                               </span>
          //                             </>
          //                           )
          //                           : (
          //                             <div className="flex flex-col items-end">
          //                               <div>
          //                                 <span>${addCommasToNumber(item.deal_amount || 0)}M</span>
          //                                 <span className="text-sm font-normal text-gray-500">
          //                                   {' '}({(((item.deal_amount || 0) / (yearData.total?.deal_amount || 1)) * 100).toFixed(1)}% of total)
          //                                 </span>
          //                               </div>
          //                               <div className="text-sm mt-1">
          //                                 <span>{(item.deal_count || item.value || 0).toLocaleString()} deals</span>
          //                                 <span className="text-sm font-normal text-gray-500">
          //                                   {' '}({(((item.deal_count || item.value || 0) / (yearData.total?.deal_count || 1)) * 100).toFixed(1)}% of total)
          //                                 </span>
          //                               </div>
          //                             </div>
          //                           )
          //                         }
          //                       </div>
          //                     </div>
          //                   </div>
          //                 </div>
          //               ))}
          //             </div>
          //           </div>
          //         ))}
          //       </div>
          //     </div>
          //   </div>
          // ) 
          : (
            <div className="w-full h-full overflow-auto">
              <div className="bg-white h-full">
                <div className="overflow-auto h-full">
                  {/* Simple Table for no breakdown - just Year, Deals, Amount */}
                  {((!hasBreakdown || isShowByYear)) ? (
                    (() => {
                      // Check if amount column should be shown (total amount > 0)
                      const showAmountColumn = (summaryData?.totals?.deal_amount || 0) > 0;
                      const effectiveValueBy = valueBy === 'both' && !showAmountColumn ? 'deals' : valueBy;
                      
                      return (
                        <div className={`flex justify-center items-center ${isMobile ? 'p-2' : 'p-8'}`}>
                          <div className="w-auto" style={{ 
                            minWidth: isMobile ? '90%' : (effectiveValueBy === 'both' ? '700px' : '500px'), 
                            maxWidth: isMobile ? '95vw' : (effectiveValueBy === 'both' ? '900px' : '700px')
                          }}>
                            <table className="w-full border-collapse shadow-lg text-xs md:text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} text-center ${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-gray-800 uppercase tracking-wider border-b-2 border-gray-300`}>
                                    <button
                                      onClick={() => handleSort('year')}
                                      className="flex items-center justify-center space-x-1 hover:text-[#bc8b20] transition-colors mx-auto"
                                    >
                                      <span>{getPeriodLabel()}</span>
                                      <span className="text-xs">
                                        {sortConfig.key === 'year' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                      </span>
                                    </button>
                                  </th>
                                  {(effectiveValueBy === 'deals' || effectiveValueBy === 'both') && (
                                    <th className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} text-center ${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-gray-800 uppercase tracking-wider border-b-2 border-gray-300`}>
                                      <button
                                        onClick={() => handleSort('deals')}
                                        className="flex items-center justify-center space-x-1 hover:text-[#bc8b20] transition-colors mx-auto"
                                      >
                                        <span>No. of Deals</span>
                                        <span className="text-xs">
                                          {sortConfig.key === 'deals' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </span>
                                      </button>
                                    </th>
                                  )}
                                  {(effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn && (
                                    <th className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} text-center ${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-gray-800 uppercase tracking-wider border-b-2 border-gray-300`}>
                                      <button
                                        onClick={() => handleSort('amount')}
                                        className="flex items-center justify-center space-x-1 hover:text-[#bc8b20] transition-colors mx-auto"
                                      >
                                        <span>Amount (US$ M)</span>
                                        <span className="text-xs">
                                          {sortConfig.key === 'amount' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </span>
                                      </button>
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {[...chartDataArray].sort((a, b) => {
                                  if (!sortConfig.key) return 0;
                                  
                                  let aValue, bValue;
                                  if (sortConfig.key === 'year') {
                                    aValue = a.label;
                                    bValue = b.label;
                                  } else if (sortConfig.key === 'deals') {
                                    aValue = a.total?.deal_count || a.total || 0;
                                    bValue = b.total?.deal_count || b.total || 0;
                                  } else if (sortConfig.key === 'amount') {
                                    aValue = a.total?.deal_amount || 0;
                                    bValue = b.total?.deal_amount || 0;
                                  }
                                  
                                  if (sortConfig.direction === 'asc') {
                                    return aValue > bValue ? 1 : -1;
                                  } else {
                                    return aValue < bValue ? 1 : -1;
                                  }
                                }).map((yearData, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-900 text-center`}>
                                      {formatPeriodLabel(String(yearData.label))}
                                    </td>
                                    {(effectiveValueBy === 'deals' || effectiveValueBy === 'both') && (
                                      <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} text-center text-gray-900`}>
                                        {(yearData.total?.deal_count || yearData.total || 0) > 0 && redirectPath ? (
                                          <a
                                            href={redirectPath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => handleDealClick(yearData.deal_ids, yearData.company_ids, `${period}`, yearData.label, true)}
                                            className="text-[#bc8b20] hover:underline cursor-pointer"
                                          >
                                            {(yearData.total?.deal_count || yearData.total || 0).toLocaleString()}
                                          </a>
                                        ) : (
                                          (yearData.total?.deal_count || yearData.total || 0).toLocaleString()
                                        )}
                                      </td>
                                    )}
                                    {(effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn && (
                                      <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} text-center text-gray-900`}>
                                        {addCommasToNumber(yearData.total?.deal_amount || 0)}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                                {/* Total Row */}
                                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                                  <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} text-gray-900 text-center`}>
                                    Total
                                  </td>
                                  {(effectiveValueBy === 'deals' || effectiveValueBy === 'both') && (
                                    <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} text-center text-gray-900`}>
                                      {(summaryData?.totals?.deal_count || 0).toLocaleString()}
                                    </td>
                                  )}
                                  {(effectiveValueBy === 'amount' || effectiveValueBy === 'both') && showAmountColumn && (
                                    <td className={`${isMobile ? 'px-2 py-2' : 'px-8 py-3'} ${isMobile ? 'text-xs' : 'text-sm'} text-center text-gray-900`}>
                                      {addCommasToNumber(summaryData?.totals?.deal_amount || 0)}
                                    </td>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    /* Complex Table with breakdown */
                  <table className="min-w-full border-collapse">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider border-b border-gray-200 sticky left-0 bg-gray-50 min-w-[120px]">
                          <div className="flex flex-col items-center space-y-2 w-full">
                            <button
                              onClick={() => handleSort(showBy || 'industry')}
                              className="flex items-center space-x-1 hover:text-gray-600 text-center leading-tight font-semibold text-sm normal-case"
                            >
                              <span>{getShowByLabel(showBy)}</span>
                              <span className="text-xs">
                                {sortConfig.key === (showBy || 'industry') ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </button>
                          </div>
                        </th>
                        {/* Generate column headers for all years */}
                        {chartCategories.map(year => (
                          <th key={year} className="px-4 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider border-b border-gray-200 min-w-[120px] bg-gray-50">
                            <div className="flex flex-col items-center space-y-2 w-full">
                              <span className="text-center leading-tight font-semibold text-sm">{formatPeriodLabel(String(year))}</span>
                              {valueBy === "both" ? (
                                <div className="flex flex-row gap-10 justify-center items-center">
                                  <button
                                    onClick={() => handleSort(`${year}_deals`)}
                                    className="flex items-center space-x-1 hover:text-gray-600 text-center leading-tight font-semibold text-sm normal-case"
                                  >
                                    <span>No. of Deals</span>
                                    <span className="text-xs">
                                      {sortConfig.key === `${year}_deals` ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                    </span>
                                  </button>
                                  <button
                                    onClick={() => handleSort(`${year}_amount`)}
                                    className="flex items-center space-x-1 hover:text-gray-600 text-center leading-tight font-semibold text-sm normal-case"
                                  >
                                    <span>Amount ($M)</span>
                                    <span className="text-xs">
                                      {sortConfig.key === `${year}_amount` ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                    </span>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSort(year)}
                                  className="flex items-center space-x-1 hover:text-gray-600 text-center leading-tight font-semibold text-sm normal-case"
                                >
                                  <span>{valueBy === "amount" ? "Amount (US$ M)" : "No. of Deals"}</span>
                                  <span className="text-xs">
                                    {sortConfig.key === year ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                  </span>
                                </button>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {tableData.map((rowData, rowIndex) => (
                        <tr key={rowData.industry} className={`transition-colors ${
                          rowData.isTotal 
                            ? 'bg-gray-100 font-bold border-t-2 border-gray-300' 
                            : rowIndex < tableData.length - 2 ? 'border-b border-gray-200 hover:bg-gray-50' : 'hover:bg-gray-50'
                        }`}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-100 z-10 border-r border-gray-200">
                            {rowData.isTotal ? (
                              <div className="font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded">Total</div>
                            ) : (
                              <div className="font-medium text-gray-900">
                                {rowData.industry}
                              </div>
                            )}
                          </td>
                          {/* Generate cells for each year */}
                          {chartCategories.map(year => {
                            const yearItemData = chartDataArray.find(item => item.label === year);
                            const breakdownItem = rowData.isTotal
                              ? null
                              : yearItemData?.breakdown?.find(b => b.group === rowData.industry);
                            const clickDealIds = breakdownItem?.deal_ids ?? yearItemData?.deal_ids;
                            const clickCompanyIds = breakdownItem?.company_ids ?? yearItemData?.company_ids;
                            const clickGroup = breakdownItem ? `${breakdownItem?.group}` ?? rowData.industry : ``;
                            if (valueBy === "both") {
                              const dealCount = rowData[`${year}_deals`] || 0;
                              const dealAmount = rowData[`${year}_amount`] || 0;
                              return (
                                <td key={year} className={`px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center ${
                                  rowData.isTotal ? 'font-bold bg-gray-100' : ''
                                }`}>
                                  <div className="flex flex-row gap-10 justify-center items-center">
                                    <div className="text-sm min-w-[80px]">
                                      {dealCount > 0 ? (
                                        redirectPath && !rowData.isTotal ? (
                                          <a
                                            href={redirectPath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => handleDealClick(clickDealIds, clickCompanyIds, clickGroup, year)}
                                            className="text-[#bc8b20] hover:underline cursor-pointer"
                                          >
                                            {dealCount.toLocaleString()}
                                          </a>
                                        ) : dealCount.toLocaleString()
                                      ) : '-'}
                                    </div>
                                    <div className="text-sm  min-w-[80px]">
                                      {dealAmount > 0 ? `${addCommasToNumber(dealAmount)}` : '-'}
                                    </div>
                                  </div>
                                </td>
                              );
                            } else {
                              const dealCount = rowData[year] || 0;
                              return (
                                <td key={year} className={`px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center ${
                                  rowData.isTotal ? 'font-bold bg-gray-100' : ''
                                }`}>
                                  <div className="font-semibold">
                                    {dealCount > 0 ? (
                                      valueBy === "amount"
                                        ? `${addCommasToNumber(dealCount)}`
                                        : (
                                          redirectPath && !rowData.isTotal ? (
                                            <a
                                              href={redirectPath}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={() => handleDealClick(clickDealIds, clickCompanyIds, clickGroup, year)}
                                              className="text-[#bc8b20] hover:underline cursor-pointer"
                                            >
                                              {dealCount.toLocaleString()}
                                            </a>
                                          ) : dealCount.toLocaleString()
                                        )
                                    ) : '-'}
                                  </div>
                                </td>
                              );
                            }
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartPopup;